// See: https://github.com/react-dropzone/react-dropzone/blob/c36ab5bd8b8fd74e2074290d80e3ecb93d26b014/src/utils/index.js#L292
function isMIMEType(value: string) {
  return ['audio/*', 'video/*', 'image/*', 'text/*'].includes(value) || /\w+\/[-+.\w]+/g.test(value);
}

// See: https://github.com/react-dropzone/react-dropzone/blob/c36ab5bd8b8fd74e2074290d80e3ecb93d26b014/src/utils/index.js#L306
function isExt(value: string) {
  return /^.*\.\w+$/.test(value);
}

// See: https://github.com/react-dropzone/react-dropzone/blob/c36ab5bd8b8fd74e2074290d80e3ecb93d26b014/src/utils/index.js#L243
function getAcceptAttr(type: keyof typeof MimeTypes) {
  return (
    Object.entries(MimeTypes[type])
      .reduce((acc, [mimeType, ext]) => acc.concat([mimeType, ...ext]), [] as string[])
      // Silently discard invalid entries as pickerOptionsFromAccept warns about these
      .filter((value) => isMIMEType(value as string) || isExt(value as string))
      .join(',')
  );
}

export const MimeType = {
  APPLICATION_DOC: 'application/msword',
  APPLICATION_DOCX: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  APPLICATION_JSON: 'application/json',
  APPLICATION_PDF: 'application/pdf',
  APPLICATION_XLS: 'application/vnd.ms-excel',
  APPLICATION_XLSX: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',

  AUDIO_M4A: 'audio/m4a',
  AUDIO_MP3: 'audio/mp3',
  AUDIO_MPEG: 'audio/mpeg',
  AUDIO_OGG: 'audio/ogg',
  AUDIO_WAV: 'audio/wav',
  AUDIO_WEBM: 'audio/webm',
  AUDIO_XM4A: 'audio/x-m4a',

  FONT_OTF: 'font/otf',
  FONT_TTF: 'font/ttf',
  FONT_WOFF: 'font/woff',
  FONT_WOFF2: 'font/woff2',

  IMAGE_AVIF: 'image/avif',
  IMAGE_GIF: 'image/gif',
  IMAGE_JPEG: 'image/jpeg',
  IMAGE_PNG: 'image/png',
  IMAGE_SVG_XML: 'image/svg+xml',
  IMAGE_WEBP: 'image/webp',

  TEXT_CSV: 'text/csv',
  TEXT_HTML: 'text/html',
  TEXT_MARKDOWN: 'text/markdown',
  TEXT_PLAIN: 'text/plain',
  TEXT_SRT: 'text/srt',

  VIDEO_M4V: 'video/m4v',
  VIDEO_MP4: 'video/mp4',
  VIDEO_QUICKTIME: 'video/quicktime',
  VIDEO_WEBM: 'video/webm',
  VIDEO_X_M4V: 'video/x-m4v',
};

const VideoMimeTypes = {
  [MimeType.VIDEO_X_M4V]: ['.m4v'],
  [MimeType.VIDEO_M4V]: ['.m4v'],
  [MimeType.VIDEO_MP4]: ['.mp4', '.m4v'],
  [MimeType.VIDEO_QUICKTIME]: ['.mov'],
  [MimeType.VIDEO_WEBM]: ['.webm'],
};

const AudioMimeTypes = {
  [MimeType.AUDIO_M4A]: ['.m4a'],
  // `.m4a` is mapped to both `audio/m4a` and `audio/x-m4a` for compatibility with systems that recognize either MIME type.
  [MimeType.AUDIO_XM4A]: ['.m4a'],
  [MimeType.AUDIO_MPEG]: ['.mp3'],
  [MimeType.AUDIO_OGG]: ['.oga', '.ogg'],
  [MimeType.AUDIO_WAV]: ['.wav'],
  [MimeType.AUDIO_WEBM]: ['.webm'],
};

const FontMimeTypes = {
  [MimeType.FONT_TTF]: ['.ttf'],
  [MimeType.FONT_OTF]: ['.otf'],
  [MimeType.FONT_WOFF]: ['.woff'],
  [MimeType.FONT_WOFF2]: ['.woff2'],
};

const ImageMimeTypes = {
  [MimeType.IMAGE_PNG]: ['.png'],
  [MimeType.IMAGE_JPEG]: ['.jpg', '.jpeg'],
  [MimeType.IMAGE_SVG_XML]: ['.svg'],
  [MimeType.IMAGE_WEBP]: ['.webp'],
  [MimeType.IMAGE_AVIF]: ['.avif'],
};

const DocumentMimeTypes = {
  [MimeType.APPLICATION_PDF]: ['.pdf'],
};

const SubtitleMimeTypes = {
  [MimeType.TEXT_SRT]: ['.srt'],
};

const PromptToStoryBoardMimeTypes = {
  [MimeType.APPLICATION_DOC]: ['.doc'],
  [MimeType.APPLICATION_DOCX]: ['.docx'],
  [MimeType.APPLICATION_PDF]: ['.pdf'],
  [MimeType.APPLICATION_XLS]: ['.xls'],
  [MimeType.APPLICATION_XLSX]: ['.xlsx'],
  [MimeType.TEXT_CSV]: ['.csv'],
  [MimeType.TEXT_HTML]: ['.html'],
  [MimeType.TEXT_MARKDOWN]: ['.md'],
  [MimeType.TEXT_PLAIN]: ['.txt'],
};

export const MimeTypes = {
  image: ImageMimeTypes,
  video: VideoMimeTypes,
  font: FontMimeTypes,
  audio: AudioMimeTypes,
  document: DocumentMimeTypes,
  subtitle: SubtitleMimeTypes,
  promptToStory: PromptToStoryBoardMimeTypes,
};

export const AcceptTypes = {
  image: getAcceptAttr('image'),
  video: getAcceptAttr('video'),
  font: getAcceptAttr('font'),
  audio: getAcceptAttr('audio'),
  document: getAcceptAttr('document'),
  promptToStory: getAcceptAttr('promptToStory'),
};
